<template>
  <div class="best-rate-tips mb-lg">
    <div class="heading-container">
      <h2 class="rate-heading">How to Get the Best Rate</h2>
      <div class="heading-accent"></div>
    </div>
    
    <div class="tips-container">
      <div v-for="(tip, index) in formattedTips" :key="index" 
           class="tip-card staggered-list-item" 
           :style="{ animationDelay: `${0.1 * index}s` }">
        <div class="tip-icon">
          <svg viewBox="0 0 24 24" class="icon" :class="`icon-${index % 5}`">
            <!-- Different icon shapes based on index -->
            <template v-if="index % 5 === 0">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" />
              <path d="M2 17L12 22L22 17" />
              <path d="M2 12L12 17L22 12" />
            </template>
            <template v-else-if="index % 5 === 1">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
              <path d="M15 9L9 15" />
              <path d="M9 9L15 15" />
            </template>
            <template v-else-if="index % 5 === 2">
              <path d="M8 12L11 15L16 10" />
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
            </template>
            <template v-else-if="index % 5 === 3">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
              <path d="M12 16V12" />
              <path d="M12 8H12.01" />
            </template>
            <template v-else>
              <path d="M9.59 4.59A2 2 0 1 1 11 8H3M9.59 4.59A2 2 0 1 1 11 8H3M9.59 4.59A2 2 0 1 1 11 8H3" />
              <path d="M21 12H3" />
              <path d="M21 16H3" />
              <path d="M21 20H3" />
            </template>
          </svg>
        </div>
        <div class="tip-content">
          <h3 class="tip-title">{{ tip.title }}</h3>
          <p class="tip-description">{{ tip.description }}</p>
        </div>
      </div>
    </div>
  </div>
  <div class="separator-container">
    <div class="separator"></div>
  </div>
</template>

<script>
export default {
  name: 'BestRateTips',
  props: {
    tips: {
      type: Array,
      required: true
    }
  },
  computed: {
    formattedTips() {
      return this.tips.map(tip => {
        if (tip.includes(':')) {
          const [title, ...descParts] = tip.split(':');
          return {
            title: title.trim(),
            description: descParts.join(':').trim()
          };
        }
        return {
          title: '',
          description: tip
        };
      });
    }
  }
};
</script>

<style scoped>
.best-rate-tips {
  background-color: var(--color-bg-subtle);
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: 0 4px 6px var(--color-shadow);
}

.tips-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .tips-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

.tip-card {
  display: flex;
  background-color: var(--color-bg-light);
  border-radius: 0.5rem;
  padding: 1.25rem;
  box-shadow: 0 2px 4px var(--color-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.tip-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px var(--color-shadow);
}

.tip-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 1rem;
}

.icon {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.icon-0 { color: var(--color-primary); }
.icon-1 { color: #3B82F6; } /* Blue */
.icon-2 { color: #10B981; } /* Green */
.icon-3 { color: #F59E0B; } /* Amber */
.icon-4 { color: #8B5CF6; } /* Purple */

.tip-content {
  flex: 1;
}

.tip-title {
  font-family: var(--font-sans);
  font-weight: var(--font-bold);
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--color-text-dark);
}

.tip-description {
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text-light);
}

.separator-container {
  display: flex;
  justify-content: center;
  margin: 3rem 0;
}

.separator {
  height: 4px;
  width: 80%;
  background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  border-radius: 2px;
}

.heading-container {
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
}

.rate-heading {
  font-family: var(--font-serif);
  font-size: 1.75rem;
  font-weight: var(--font-bold);
  color: var(--color-text-dark);
  margin-bottom: 0.5rem;
  position: relative;
  display: inline-block;
}

.heading-accent {
  width: 80px;
  height: 4px;
  background-color: var(--color-primary);
  margin: 0 auto;
  border-radius: 2px;
}
</style> 